import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Task from '../../entities/task';
import Invoice from '../../entities/invoice';
import Comment from '../../entities/comment';
import Document from '../../entities/document';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteComment, collectCommentDeleteImpact} from './commentDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
export function canDeleteTask(): boolean {
return canCascadeDelete();
}
export function collectTaskDeleteImpact(task: Task, context: DeleteImpactContext): void {
const key = 'task:' + task.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
if (!!task.invoiceId) {
addDeleteImpact(context, key, 'tasks*');
return;
}
const invoiceTaskIdsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.taskIds?.includes(task.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceTaskIdsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));
const invoiceTaskPositionsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.taskPositions?.includes(task.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceTaskPositionsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'task' && comment.entityId === task.id).forEach(comment => {
if (context.visited.has('comment:' + comment.id)) return;
addDeleteImpact(context, 'comment:' + comment.id, 'comments');
collectCommentDeleteImpact(comment, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'task' && document.entityId === task.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
}
export function deleteImpactTask(task: Task): DeleteImpact {
const context = deleteImpactContext();
collectTaskDeleteImpact(task, context);
return deleteImpactCounts(context);
}
export function deleteTask(task: Task, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (cascade && (!!task.invoiceId)) return {status: 'blocked', impacts: [{value: 'tasks*', count: 1}]};
if (!cascade) {
if (!canDeleteTask()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectTaskDeleteImpact(task, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || task.id};
}
const key = 'task:' + task.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'task' && comment.entityId === task.id).forEach(comment => {deleteComment(comment, mergeId, cascade);});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'task' && document.entityId === task.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('taskIds', task.id), ...store.getState().invoices.getMappingFor('taskPositions', task.id)]).filter(invoiceTemp => invoiceTemp.taskIds?.includes(task.id) || invoiceTemp.taskPositions?.includes(task.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.taskIds = _invoice.taskIds?.filter(id => id !== task.id) || [];

_invoice.taskPositions = _invoice.taskPositions?.filter(id => id !== task.id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});
const deleted = task.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.TaskActions.removeTaskQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceTask(task: Task, replaceWith: Task, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = task.id;}
const _work = workingSet();
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('taskIds', task.id), ...store.getState().invoices.getMappingFor('taskPositions', task.id)]).filter(invoiceTemp => invoiceTemp.taskIds?.includes(task.id) || invoiceTemp.taskPositions?.includes(task.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.taskIds = _invoice.taskIds?.map(id => (id === task.id) ? replaceWith.id : id) || [];

_invoice.taskPositions = _invoice.taskPositions?.map(id => (id === task.id) ? replaceWith.id : id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});



workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'task' && comment.entityId === task.id).forEach(commentTemp => {
const _comment = ((_work.get('comment:' + commentTemp.id) ?? commentTemp) as Comment).clone();
if (_comment.status === 'D') return;
_comment.entityId = replaceWith.id;
_comment.status = 'U';
_comment.addedOn = newHRTimestamp();
_comment.addedBy = getCurrentUserId();
_comment.importSource = targetImportSource;
_comment.importId = mergeId;
_work.set('comment:' + _comment.id, _comment);
store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'task' && document.entityId === task.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
return task;
}
